//
// ============================================================================
// IMPORTS
// ============================================================================
//

import type { PendingSample } from "../../../services/api/domains/pending/pending.types";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Validation Keyboard Navigation Composable
 *
 * Provides keyboard shortcuts for rapid sample navigation.
 *
 * Responsibilities:
 * - Handle ArrowUp/Down keys for sample navigation
 * - Prevent navigation when dialogs are open
 * - Support wraparound navigation (first <-> last)
 * - Respect filtered sample list for navigation
 * - Debounce rapid key-hold navigation to avoid request waterfalls
 *
 * @param filteredSamples - Computed array of visible samples
 * @param selectedSample - Currently selected sample ref
 * @param selectSample - Function to select a sample (immediate, programmatic)
 * @param handleSampleClick - Debounced click handler (visual-first + debounced fetch)
 */
export const useValidationKeyboard = (
  filteredSamples: ComputedRef<PendingSample[]>,
  selectedSample: Ref<PendingSample | null>,
  selectSample: (sample: PendingSample | null) => void,
  handleSampleClick: (sample: PendingSample) => void,
) => {
  /**
   * Check if any Vuetify dialog is currently open
   *
   * Purpose:
   * - Prevent keyboard navigation when user is interacting with dialogs
   * - Query DOM for visible v-dialog elements
   *
   * Responsibilities:
   * - Check for any .v-dialog elements without .v-dialog--hidden class
   * - Return true if at least one dialog is visible
   *
   * @returns True if any dialog is visible
   */
  const isAnyDialogOpen = (): boolean => {
    // Check if any v-dialog element is visible in DOM
    const dialogs = document.querySelectorAll(".v-dialog:not(.v-dialog--hidden)");
    return dialogs.length > 0;
  };

  const isTypingTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false;
    const tagName = target.tagName.toLowerCase();
    return tagName === "input" || tagName === "textarea" || tagName === "select" || target.isContentEditable;
  };

  /**
   * Handle keyboard shortcuts for rapid navigation
   *
   * Purpose:
   * - Enable keyboard-only sample navigation
   * - Provide power user workflow optimization
   * - Support wraparound navigation at list boundaries
   *
   * Responsibilities:
   * - Listen for ArrowUp/ArrowDown keys
   * - Block navigation if dialogs are open
   * - Prevent default scroll behavior
   * - Calculate next index with wraparound
   * - Invoke selectSample with next sample
   *
   * Navigation Rules:
   * - ArrowUp: Previous sample (wrap to last if at first)
   * - ArrowDown: Next sample (wrap to first if at last)
   * - Disabled when: Any dialog is visible
   * - Disabled when: No samples or no selection
   *
   * @param event - Keyboard event
   */
  const handleKeyDown = async (event: KeyboardEvent) => {
    // Only handle arrow keys
    if (!["ArrowUp", "ArrowDown"].includes(event.key)) return;

    // Block navigation if any dialog is open or the user is typing
    if (isAnyDialogOpen() || isTypingTarget(event.target)) {
      return;
    }

    // Prevent default scroll behavior
    event.preventDefault();

    // Guard: No samples or no selection
    if (!selectedSample.value || filteredSamples.value.length === 0) return;

    // Find current index
    const currentIndex = filteredSamples.value.findIndex((s) => s.barcode === selectedSample.value!.barcode);

    if (currentIndex === -1) return;

    // Calculate next index with wrapping
    let nextIndex: number;

    if (event.key === "ArrowDown") {
      // Wrap to first sample
      nextIndex = currentIndex === filteredSamples.value.length - 1 ? 0 : currentIndex + 1;
    } else {
      // ArrowUp - Wrap to last sample
      nextIndex = currentIndex === 0 ? filteredSamples.value.length - 1 : currentIndex - 1;
    }

    // Select next sample using the debounced handler:
    // - moves visual highlight immediately
    // - aborts any in-flight requests
    // - delays the fetch until key activity stops (same 120ms window as mouse)
    const nextSample = filteredSamples.value[nextIndex];
    if (nextSample) {
      handleSampleClick(nextSample);
    }
  };

  //
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  //

  return {
    handleKeyDown,
  };
};
